import { useQuery } from "@tanstack/react-query";
import { systemService } from "@/services/systemService";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Activity, AlertCircle } from "lucide-react";
import { POLLING } from "@/lib/constants";

interface HealthStatusProps {
    variant?: "badge" | "icon";
}

export const HealthStatus = ({ variant = "badge" }: HealthStatusProps) => {
    const { data: health } = useQuery({
        queryKey: ["system", "health"],
        queryFn: systemService.checkHealth,
        refetchInterval: POLLING.HEALTH_CHECK,
        retry: false,
    });

    const isHealthy = health?.api === "healthy";
    const statusLabel = isHealthy ? "System Online" : "System Offline";

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <div
                    aria-label={statusLabel}
                    className={
                        variant === "icon"
                        ? `flex items-center justify-center h-8 w-8 md:h-9 md:w-9 rounded-full cursor-help transition-colors ${isHealthy
                                ? "text-green-600 bg-green-50/50 border border-green-100"
                                : "text-red-600 bg-red-50/50 border border-red-100"
                            }`
                            : `flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium cursor-help transition-colors ${isHealthy
                                ? "text-green-600 bg-green-50/50 border border-green-100"
                                : "text-red-600 bg-red-50/50 border border-red-100"
                            }`
                    }
                >
                    {isHealthy ? (
                        <Activity className={variant === "icon" ? "h-3.5 w-3.5 md:h-4 md:w-4" : "h-3 w-3"} />
                    ) : (
                        <AlertCircle className={variant === "icon" ? "h-3.5 w-3.5 md:h-4 md:w-4" : "h-3 w-3"} />
                    )}
                    {variant !== "icon" && (
                        <span className="hidden sm:inline">{statusLabel}</span>
                    )}
                </div>
            </TooltipTrigger>
            <TooltipContent>
                <div className="space-y-1 text-xs">
                    <p className="font-semibold">System Status</p>
                    <div className="flex items-center gap-2">
                        <div className={`h-2 w-2 rounded-full ${health?.api === "healthy" ? "bg-green-500" : "bg-red-500"}`} />
                        <span>API: {health?.api || "Unknown"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className={`h-2 w-2 rounded-full ${health?.database === "healthy" ? "bg-green-500" : "bg-red-500"}`} />
                        <span>Database: {health?.database || "Unknown"}</span>
                    </div>
                </div>
            </TooltipContent>
        </Tooltip>
    );
};
